import React, { useEffect } from "react";
import { Helmet } from "react-helmet";

import VisionArt from "../assets/vision.svg";
import HostelArt from "../assets/hostel.svg";
import ProcessArt from "../assets/process.svg";

import WebIcon from "@mui/icons-material/Language";
import AppIcon from "@mui/icons-material/PhoneIphone";
import SeoIcon from "@mui/icons-material/TrendingUp";
import BotIcon from "@mui/icons-material/AutoAwesome";

import EditNoteIcon from "@mui/icons-material/EditNote";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";

import Industries from "./Industries";
import ContactForm from "../components/ContactForm";
import HeroCarousel from "../components/HeroCarousel";
import ReviewsSection from "../components/ReviewsSection";

const services = [
  {
    key: "web",
    title: "Web Development",
    desc: "High-performance websites and web apps built with modern frameworks and clean architecture.",
    bullets: [
      "Responsive UI and accessibility (WCAG)",
      "API-first, secure and scalable",
    ],
    icon: WebIcon,
  },
  {
    key: "mobile",
    title: "Mobile App Development",
    desc: "Native and cross-platform mobile apps that feel fast and polished on every device.",
    bullets: [
      "React Native/Flutter + native modules",
      "App Store/Play Store ready builds & CI/CD",
    ],
    icon: AppIcon,
  },
  {
    key: "seo",
    title: "SEO Optimization",
    desc: "Data-driven SEO to grow qualified traffic and conversions.",
    bullets: [
      "Technical SEO audits & fixes",
      "Content strategy, schema, Core Web Vitals",
    ],
    icon: SeoIcon,
  },
  {
    key: "automation",
    title: "Web Scraping & Automation",
    desc: "Reliable data pipelines and task automation to save hours every week.",
    bullets: [
      "Headless/browserless scraping with anti-bot handling",
      "Dashboards, alerts and exports (CSV/JSON)",
    ],
    icon: BotIcon,
  },
];

const steps = [
  {
    title: "Fill Out The Contact Form",
    desc: "Share your goals and requirements so we can understand your needs.",
    icon: EditNoteIcon,
  },
  {
    title: "Meet With Our Specialist",
    desc: "Discuss solutions, timelines, and budget in a quick discovery call.",
    icon: SupportAgentIcon,
  },
  {
    title: "Start Project",
    desc: "Kick off with a clear roadmap, milestones, and communication plan.",
    icon: RocketLaunchIcon,
  },
];

function Home() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <Helmet>
        <title>MockingJay - Web & Mobile App Development | SEO Services</title>
        <meta
          name="description"
          content="MockingJay offers top-tier web and mobile app development, alongside expert SEO services. We build high-performance, scalable solutions to grow your business."
        />
        <meta
          name="keywords"
          content="web development, mobile app development, seo services, react native, flutter, node.js, react.js, custom software development"
        />
      </Helmet>

      {/* ================= HERO SECTION ================= */}
      <section className="relative bg-gradient-to-br from-primary/10 via-accent to-primary/20">
        {/* Tagline exactly between Navbar and Hero Heading (no overlap)
        <div className="pt-6 sm:pt-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center sm:justify-start">
              <span
                className="
                  inline-flex items-center gap-2
                  px-4 py-1.5
                  rounded-full
                  border border-secondary
                  bg-white/70 backdrop-blur-sm
                  text-secondary
                  text-sm sm:text-base
                  font-semibold
                  shadow-sm
                  tracking-wide
                "
              >
                <span className="h-2 w-2 rounded-full bg-secondary" />
                We Build What You Think. We Deliver What You Need.
              </span>
            </div>
          </div>
        </div> */}

        {/* Hero Carousel */}
        <div className="max-h-[calc(100vh-64px)] overflow-hidden">
          <HeroCarousel />
        </div>
      </section>
      {/* ================= END HERO SECTION ================= */}

      {/* Services */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-secondary text-sm font-medium border border-secondary shadow-sm">
              What We Do
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-4 text-secondary">
              We Provide The Best Services
            </h2>
            <p className="mt-2 text-lg sm:text-xl text-secondary/80">
              Our expert team delivers a wide range of services to meet your
              business needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map(({ key, title, desc, bullets, icon: Icon }) => (
              <div
                key={key}
                className="relative rounded-xl text-secondary border border-secondary p-6 shadow-sm hover:shadow-md hover:bg-primary transition hover:text-secondary hover:border-primary"
              >
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-12 h-12 rounded-lg bg-secondary text-accent flex items-center justify-center">
                    <Icon fontSize="large" />
                  </div>

                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold">{title}</h3>
                    <p className="mt-1">{desc}</p>

                    <ul className="mt-3 space-y-2 text-sm">
                      {bullets.map((b, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="mt-2 inline-block h-1.5 w-1.5 rounded-full bg-secondary" />
                          <span className="break-words">{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects */}
      <section
        id="projects"
        className="py-20 bg-gradient-to-br from-accent via-primary/20 to-primary/10"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-secondary text-sm font-medium border border-secondary shadow-sm">
              Products
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-4 text-secondary">
              Products We've Built
            </h2>
            <p className="mt-2 text-lg sm:text-xl text-secondary/80">
              Explore some of our most successful products.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="rounded-2xl border border-secondary overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <img
                src={HostelArt}
                alt="Hostel & Study center Management"
                className="w-full h-full object-contain rounded-lg"
              />
            </div>

            <div>
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <h3 className="text-2xl sm:text-3xl font-semibold text-secondary text-center sm:text-left">
                  Hostel & Study center Management
                </h3>
                <span className="px-3 py-1 text-sm rounded-full text-secondary border border-secondary shadow-sm">
                  Live
                </span>
              </div>

              <p className="mt-4 text-center sm:text-left">
                A comprehensive web application that simplifies student bookings
                and end-to-end administration for hostels and study center.
              </p>

              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2 text-md">
                {[
                  "Live seat mapping",
                  "Student bed booking",
                  "Study center seat booking",
                  "Online fee payments",
                  "Admin: room/bed & seat management",
                  "Fees, complaints, reviews",
                  "Complete booking history",
                  "Email notifications",
                  "Revenue Model",
                  "Deposite tracking",
                ].map((t) => (
                  <li key={t} className="flex items-center gap-3">
                    <span className="inline-block h-2 w-2 rounded-full bg-secondary" />
                    {t}
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex justify-center sm:justify-start gap-4">
                <a
                  href="https://bussinesstrack.in/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-5 py-3 rounded-md bg-secondary text-accent hover:bg-primary hover:text-secondary shadow-md transition-all duration-300 transform hover:-translate-y-1"
                >
                  Open Live
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <Industries />


      {/* Process */}
      <section
        className="py-20 bg-gradient-to-br from-primary/10 via-accent to-primary/20"
        id="work"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-secondary text-sm font-medium border border-secondary shadow-sm">
              Process
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-4 text-secondary">
              Simple Working Process
            </h2>
            <p className="mt-2 text-lg sm:text-xl text-secondary/80">
              Our streamlined process ensures a smooth and efficient journey
              from start to finish.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute left-6 top-0 bottom-0 hidden md:block border-l-2 border-secondary" />
              <div className="space-y-8">
                {steps.map(({ title, desc, icon: Icon }) => (
                  <div key={title} className="relative flex gap-6 items-start">
                    <div className="relative z-10 shrink-0">
                      <div className="w-14 h-14 rounded-full bg-secondary text-accent flex items-center justify-center shadow-md">
                        <Icon />
                      </div>
                    </div>

                    <div className="border border-secondary text-secondary hover:bg-primary hover:text-secondary hover:border-primary rounded-xl p-5 shadow-lg w-full hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                      <h3 className="text-xl font-semibold">{title}</h3>
                      <p className="mt-2 text-md">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-secondary overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <img
                src={ProcessArt}
                alt="Process"
                className="w-full h-full object-contain rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Vision */}
      <section id="vision" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="overflow-hidden rounded-2xl border border-secondary shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <img
                src={VisionArt}
                alt="Vision"
                className="w-full h-full object-contain rounded-lg"
              />
            </div>
          </div>

          <div>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-secondary text-sm font-medium border border-secondary shadow-sm">
              Our Mission & Vision
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-secondary mt-4">
              Empowering Innovations
            </h2>
            <p className="mt-4 text-lg sm:text-xl text-secondary leading-relaxed">
              Our vision is to revolutionize the digital landscape through
              innovative solutions. We aspire to be the driving force behind
              transformative technology, empowering businesses to thrive in the
              digital era. Our commitment is to lead with creativity and
              unwavering dedication to our clients' success.
            </p>

            <ul className="mt-6 grid xs:grid-cols-2 grid-cols-1 gap-4 text-md">
              {[
                "Innovation-first approach",
                "Human-centered design",
                "Secure, scalable solutions",
                "Measurable business outcomes",
              ].map((t) => (
                <li key={t} className="flex items-center gap-3">
                  <span className="text-secondary text-xl">✔</span>
                  <span className="text-secondary">{t}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-4">
              <a
                href="#contact"
                className="w-full sm:w-auto px-5 py-3 rounded-md border border-secondary bg-secondary text-accent hover:bg-primary hover:text-secondary hover:border-primary shadow-md transition-all duration-300 transform hover:-translate-y-1"
              >
                Let's Connect
              </a>
              <a
                href="#work"
                className="w-full sm:w-auto px-5 py-3 rounded-md border border-secondary text-secondary hover:bg-primary hover:text-secondary hover:border-primary shadow-sm transition-all duration-300 transform hover:-translate-y-1"
              >
                Our Process
              </a>
            </div>
          </div>
        </div>
      </section>



      {/* Contact */}
      <ContactForm />
      {/* Reviews */}
      <ReviewsSection />
    </div>
  );
}

export default Home;
